import styled from '@emotion/styled';
import { sdsColor } from '@staging-design-system/tokens';
import { rem } from '@utils';

const buttonIconStyles = `
  svg {
    flex-shrink: 0;
    height: ${rem(16)};
    width: ${rem(16)};
  }

  svg path {
    fill: currentColor;
  }
`;

const Header = styled.div<{ isExpanded: boolean }>(
  ({ isExpanded }) => `
  align-items: center;
  display: flex;
  justify-content: ${isExpanded ? 'space-between' : 'center'};
  margin: ${rem(16)} 0 ${rem(8)};
  min-height: ${rem(24)};
  padding: 0 ${rem(isExpanded ? 16 : 0)};
`
);

const Logo = styled.div<{ isExpanded: boolean }>(
  ({ isExpanded }) => `
  display: ${isExpanded ? 'flex' : 'none'};

  svg {
    height: ${rem(16)};
    width: ${rem(51)};
  }
`
);

const HeaderToggle = styled.button(
  ({ theme }) => `
  align-items: center;
  background: ${sdsColor.background.defaultSecondary};
  border-radius: ${rem(4)};
  border: none;
+  box-sizing: border-box;
  color: ${sdsColor.text.defaultDefault};
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: ${rem(32)};
  justify-content: center;
  padding: ${rem(8)};
  width: ${rem(32)};

  ${buttonIconStyles}

  &:hover {
    background: ${sdsColor.background.defaultDefaultHover};
    color: ${sdsColor.text.defaultDefault};
  }

  &:focus-visible {
    outline-offset: ${rem(2)};
    outline: 1px solid ${theme.palette.primary.dark};
  }
`
);

const MobileHeader = styled.header`
  align-items: center;
  background: ${sdsColor.background.defaultSecondary};
  box-shadow: 0 ${rem(2)} ${rem(2)} rgba(25, 21, 48, 0.08);
  display: none;
  grid-area: c;
  justify-content: space-between;
  min-height: var(--mobile-sidebar-topbar-height);
  padding: ${rem(8)} ${rem(24)};
  position: sticky;
  top: 0;
  width: 100%;
  z-index: 4;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const MobileHeaderGroup = styled.div`
  align-items: center;
  column-gap: ${rem(16)};
  display: flex;
`;

const MobileHeaderButton = styled.button(
  ({ theme }) => `
  align-items: center;
  background: transparent;
  border-radius: ${rem(4)};
  border: none;
  color: ${sdsColor.text.defaultDefault};
  cursor: pointer;
  display: inline-flex;
  height: ${rem(32)};
  justify-content: center;
  padding: ${rem(8)};
  width: ${rem(32)};

  ${buttonIconStyles}

  &:hover {
    background: ${sdsColor.background.defaultSecondaryHover};
  }

  &:focus-visible {
    outline-offset: -${rem(2)};
    outline: 2px solid ${theme.palette.primary.dark};
  }
`
);

const MobileAvatarButton = styled.button(
  ({ theme }) => `
  align-items: center;
  background: transparent;
  border-radius: ${rem(4)};
  border: none;
  color: ${sdsColor.text.defaultDefault};
  cursor: pointer;
  display: inline-flex;
  height: ${rem(40)};
  justify-content: center;
  padding: ${rem(8)};
  width: ${rem(40)};

  > span {
    font-size: ${rem(12)};
    inline-size: ${rem(24)};
  }

  &:hover {
    background: ${sdsColor.background.defaultDefaultHover};
  }

  &:focus-visible {
    outline-offset: -${rem(2)};
    outline: 2px solid ${theme.palette.primary.dark};
  }
`
);

const AccountAvatar = styled.span(
  ({ theme }) => `
  align-items: center;
  aspect-ratio: 1;
  border-radius: 50%;
  color: ${theme.palette.common.white};
  display: inline-flex;
  flex: none;
  flex-shrink: 0;
  font-size: ${rem(10)};
  font-weight: 600;
  inline-size: ${rem(16)};
  justify-content: center;
  line-height: 1;
  overflow: hidden;
  position: relative;
  text-transform: uppercase;

  svg {
    display: block;
    height: 100%;
    width: 100%;
  }
`
);

const AccountAvatarInitial = styled.span`
  align-items: center;
  display: flex;
  inset: 0;
  justify-content: center;
  line-height: 1;
  position: absolute;
`;

const MobileLogo = styled.div`
  display: flex;

  svg {
    height: ${rem(16)};
    width: ${rem(51)};
}
`;

const MobileFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  margin-top: auto;
  padding: ${rem(8)};
  width: 100%;
`;

const Footer = styled.div<{ isExpanded: boolean }>(
  ({ isExpanded }) => `
  align-items: ${isExpanded ? 'stretch' : 'center'};
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  margin-top: auto;
  padding: ${rem(12)} ${rem(isExpanded ? 8 : 0)} ${rem(16)};
  position: relative;
`
);

const NavButtonGroup = styled.div<{ isExpanded: boolean }>(
  ({ isExpanded }) => `
  display: flex;
  align-items: ${isExpanded ? 'stretch' : 'center'};
  flex-direction: column;
  gap: ${rem(8)};
  margin-bottom: ${rem(8)};
  padding: 0 ${rem(isExpanded ? 8 : 0)};
  width: 100%;
`
);

const Sidebar = styled.aside<{
  isBusy: boolean;
  isExpanded: boolean;
}>(
  ({ isBusy, isExpanded }) => `
  background: var(--sidebar-background-color);
  box-shadow: var(--sidebar-box-shadow);
  color: var(--sidebar-color);
  cursor: ${isBusy ? 'wait' : 'default'};
  display: flex;
  flex-direction: column;
  grid-area: c;
  width: var(
    ${isExpanded ? '--sidebar-max-width' : '--sidebar-min-width'}
  );
  padding: 0;
  position: sticky;
  top: 0;
  transition: width 200ms ease-out;
  will-change: width;
  z-index: 3;

  > * {
    pointer-events: ${isBusy ? 'none' : 'auto'};
  }

  &:focus-visible {
    outline: none;
  }
`
);

type MobileDrawerAnchor = 'left' | 'right';

const MobileDrawer = styled.aside<{
  anchor?: MobileDrawerAnchor;
  isBusy: boolean;
  isOpen: boolean;
}>(
  ({ anchor = 'left', isBusy, isOpen }) => `
  background: var(--sidebar-background-color);
  box-shadow: var(--sidebar-box-shadow);
  color: var(--sidebar-color);
  cursor: ${isBusy ? 'wait' : 'default'};
  display: none;
  flex-direction: column;
  height: calc(100dvh - var(--mobile-sidebar-topbar-height));
  ${anchor}: 0;
  overflow: hidden;
  padding: ${rem(16)} 0 0;
  position: fixed;
  top: var(--mobile-sidebar-topbar-height);
  transform: translate3d(${
    isOpen ? 0 : anchor === 'left' ? '-100%' : '100%'
  }, 0, 0);
  transition: transform 250ms ease-in-out, visibility 0ms ${isOpen ? 0 : 250}ms;
  visibility: ${isOpen ? 'visible' : 'hidden'};
  width: min(var(--sidebar-max-width), 100vw);
  will-change: transform;
  z-index: 3;

  > * {
    pointer-events: ${isBusy ? 'none' : 'auto'};
  }

  @media (max-width: 768px) {
    display: flex;
  }
`
);

const MobileAccountContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  padding: 0 ${rem(16)};
`;

const MobileShell = styled.div`
  display: contents;
`;

const Styled = {
  AccountAvatar,
  AccountAvatarInitial,
  Footer,
  Header,
  HeaderToggle,
  Logo,
  MobileAccountContent,
  MobileAvatarButton,
  MobileDrawer,
  MobileHeader,
  MobileHeaderButton,
  MobileHeaderGroup,
  MobileShell,
  MobileFooter,
  MobileLogo,
  NavButtonGroup,
  Sidebar
};

export default Styled;
